/**
 * OnboardingForm — orchestratore del flusso 4-step.
 *
 * Responsabilita':
 *  - Single useForm (RHF) con state {step1, step2, step3, voci, confermaPresaVisione}
 *  - Pre-popolamento da prefilled (validateOnboardingToken) e da draft localStorage
 *  - Caricamento documenti attivi dal registro (callable, niente Firestore)
 *  - Submit -> completeOnboarding con payload V2 `documenti[]`
 *  - Esito completato -> /onboarding/success
 *  - Esito consensi_rifiutati -> /onboarding/consensi-rifiutati
 *  - Errore di forma payload (finestra di deploy) -> messaggio dedicato
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  onboardingStep1Schema,
  onboardingStep2Schema,
  onboardingStep3Schema,
} from '@shared/schemas/onboarding';
import { z } from 'zod';
import type { DocumentoLegale } from '@shared/legal/documento-legale.types';
import { pazienteFunctions } from '../firebase-paziente';
import { useOnboardingDraft } from '../hooks/useOnboardingDraft';
import { normalizzaBozzaOnboarding } from '../hooks/normalizza-bozza-onboarding';
import {
  isPayloadShapeError,
  loadDocumentiLegaliAttivi,
  PAYLOAD_SHAPE_ERROR_MESSAGE,
} from '../lib/onboarding-callable';
import { idsDocumentiSenzaRisposta } from '@shared/legal/risposta-documento';
import { OnboardingShell } from './OnboardingShell';
import { OnboardingStep1 } from './OnboardingStep1';
import { OnboardingStep2 } from './OnboardingStep2';
import { OnboardingStep3 } from './OnboardingStep3';
import { OnboardingStep4 } from './OnboardingStep4';
import type { OnboardingFormValues } from './onboarding-form-types';

interface OnboardingFormProps {
  token: string;
  pazienteId: string;
  prefilled: {
    nome: string;
    cognome: string;
    email: string;
    telefono: string;
    /** Presente solo se lo staff ha già salvato M, F o altro. */
    sesso?: string;
  };
}

function sessoPrecompilato(sesso: string | undefined): 'M' | 'F' | 'altro' | '' {
  if (sesso === 'M' || sesso === 'F' || sesso === 'altro') return sesso;
  return '';
}

const fullFormSchema = z.object({
  step1: onboardingStep1Schema,
  step2: onboardingStep2Schema,
  step3: onboardingStep3Schema,
  confermaPresaVisione: z.literal(true, {
    error: 'Conferma di aver preso visione per completare.',
  }),
});

function buildDefaults(
  prefilled: OnboardingFormProps['prefilled']
): OnboardingFormValues {
  return {
    step1: {
      cognome: prefilled.cognome,
      nome: prefilled.nome,
      email: prefilled.email,
      telefono: prefilled.telefono,
      dataNascita: '',
      sesso: sessoPrecompilato(prefilled.sesso),
      luogoNascita: '',
      provinciaNascita: '',
      nazioneNascita: 'Italia',
      cf: '',
      indirizzo: '',
      citta: '',
      cap: '',
      provincia: '',
    } as OnboardingFormValues['step1'],
    step2: {
      storiaFamiliare: '',
      patologie: '',
      interventi: '',
      farmaci: '',
      allergieIntolleranze: '',
    },
    step3: {
      professione: '',
      statoCivile: '',
      figli: '',
      obiettiviConsulenza: '',
      doveMangia: '',
      alimentiSgraditi: '',
      tipoDieta: '',
      alcol: '',
      momentoFame: '',
      mangiaVeloce: false,
      fameNervosa: false,
      sport: false,
      tipoSport: '',
      orariSport: '',
      altroNote: '',
    },
    voci: {},
    confermaPresaVisione: false as unknown as true,
  };
}

function buildDocumentiPayload(
  documenti: DocumentoLegale[],
  voci: Record<string, boolean | undefined>
): Array<{
  documentoId: string;
  versione: string;
  voci: Array<{ consensoId: string; accepted: boolean }>;
}> {
  return documenti.map((doc) => ({
    documentoId: doc.documentoId,
    versione: doc.versione,
    voci: doc.consensi.map((spec) => ({
      consensoId: spec.id,
      accepted: voci[spec.id] === true,
    })),
  }));
}

type StepIndex = 1 | 2 | 3 | 4;

export function OnboardingForm({ token, prefilled }: OnboardingFormProps) {
  const navigate = useNavigate();
  const { initialDraft, hasDraft, save, clear } =
    useOnboardingDraft<OnboardingFormValues>(token);
  const [step, setStep] = useState<StepIndex>(1);
  const [submitting, setSubmitting] = useState(false);
  const [documenti, setDocumenti] = useState<DocumentoLegale[] | null>(null);
  const [documentiError, setDocumentiError] = useState(false);
  const [documentiSenzaRisposta, setDocumentiSenzaRisposta] = useState<
    string[]
  >([]);
  const [segnalazioneSeq, setSegnalazioneSeq] = useState(0);
  const draftRestoredRef = useRef(false);

  const defaults = buildDefaults(prefilled);
  const form = useForm<OnboardingFormValues>({
    resolver: zodResolver(fullFormSchema) as unknown as Resolver<OnboardingFormValues>,
    mode: 'onBlur',
    defaultValues: defaults,
  });

  const { register, control, watch, trigger, getValues, setValue, reset, formState } =
    form;
  const voci = watch('voci');

  useEffect(() => {
    if (initialDraft && !draftRestoredRef.current) {
      draftRestoredRef.current = true;
      const bozza = normalizzaBozzaOnboarding(initialDraft);
      const sessoBozza = bozza.step1?.sesso;
      const sesso =
        sessoBozza === 'M' || sessoBozza === 'F' || sessoBozza === 'altro'
          ? sessoBozza
          : sessoPrecompilato(prefilled.sesso);
      reset({
        ...bozza,
        step1: {
          ...bozza.step1,
          cognome: prefilled.cognome,
          nome: prefilled.nome,
          email: prefilled.email,
          telefono: prefilled.telefono,
          sesso,
        } as OnboardingFormValues['step1'],
      });
      if (hasDraft) {
        toast.success('Ripreso da dove avevi lasciato', { duration: 2500 });
      }
    }
  }, [initialDraft, hasDraft, reset, prefilled]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/incompatible-library -- watch() subscription pattern is correct, React Compiler false positive
    const sub = watch((values) => {
      save(values as OnboardingFormValues);
    });
    return () => sub.unsubscribe();
  }, [watch, save]);

  const loadDocumenti = useCallback(async () => {
    setDocumentiError(false);
    try {
      const docs = await loadDocumentiLegaliAttivi();
      setDocumenti(docs);
    } catch {
      setDocumenti(null);
      setDocumentiError(true);
    }
  }, []);

  useEffect(() => {
    void loadDocumenti();
  }, [loadDocumenti]);

  const trackProgress = useCallback(
    async (currentStep: number) => {
      try {
        const fn = httpsCallable<{ token: string; step: number }, { ok: true }>(
          pazienteFunctions,
          'trackOnboardingProgress'
        );
        await fn({ token, step: currentStep });
      } catch {
        // best-effort, niente UX visibile in caso di errore
      }
    },
    [token]
  );

  useEffect(() => {
    void trackProgress(step);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [step, trackProgress]);

  const stepFieldsMap: Record<StepIndex, Array<keyof OnboardingFormValues | string>> = {
    1: ['step1'],
    2: ['step2'],
    3: ['step3'],
    4: ['confermaPresaVisione'],
  };

  const handleNext = async () => {
    const fieldsToValidate = stepFieldsMap[step] as Parameters<typeof trigger>[0];
    const ok = await trigger(fieldsToValidate, { shouldFocus: true });
    if (!ok) {
      toast.error('Controlla i campi evidenziati in rosso.');
      return;
    }
    toast.success('Salvato in locale', { duration: 1500 });
    setStep((prev) => Math.min(4, prev + 1) as StepIndex);
  };

  const handlePrev = () => {
    setStep((prev) => Math.max(1, prev - 1) as StepIndex);
  };

  const onSubmit = async () => {
    if (submitting) return;
    if (!documenti || documenti.length === 0) {
      toast.error('I documenti non sono ancora disponibili. Riprova tra poco.');
      return;
    }
    const values = getValues();
    const incompleti = idsDocumentiSenzaRisposta(documenti, values.voci);
    if (incompleti.length > 0) {
      setDocumentiSenzaRisposta(incompleti);
      setSegnalazioneSeq((n) => n + 1);
      toast.error('Rispondi a tutti i documenti prima di inviare.');
      return;
    }
    setDocumentiSenzaRisposta([]);
    if (values.confermaPresaVisione !== true) {
      toast.error('Conferma di aver preso visione per completare.');
      return;
    }
    const ok = await trigger(undefined, { shouldFocus: true });
    if (!ok) {
      toast.error('Controlla i campi evidenziati in rosso.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        token,
        step1: values.step1,
        step2: values.step2,
        step3: values.step3,
        documenti: buildDocumentiPayload(documenti, values.voci),
      };
      const fn = httpsCallable<
        typeof payload,
        { ok: true; esito: 'completato' | 'consensi_rifiutati'; redirectTo: string }
      >(pazienteFunctions, 'completeOnboarding');
      const res = await fn(payload);
      clear();
      const dest =
        res.data.esito === 'consensi_rifiutati'
          ? '/onboarding/consensi-rifiutati'
          : res.data.redirectTo || '/onboarding/success';
      navigate(dest, { replace: true });
    } catch (err) {
      if (isPayloadShapeError(err)) {
        toast.error(PAYLOAD_SHAPE_ERROR_MESSAGE);
      } else {
        toast.error(
          'Si \u00e8 verificato un errore. Lo studio ti contatter\u00e0 se ci sono problemi.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OnboardingShell
      currentStep={step}
      title={step === 1 ? `Ciao ${prefilled.nome}!` : undefined}
      subtitle={
        step === 1
          ? 'Compila il modulo per preparare la tua prima visita.'
          : undefined
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (step === 4) void onSubmit();
        }}
        noValidate
      >
        {step === 1 && (
          <OnboardingStep1 register={register} errors={formState.errors} />
        )}
        {step === 2 && <OnboardingStep2 register={register} errors={formState.errors} />}
        {step === 3 && <OnboardingStep3 register={register} errors={formState.errors} />}
        {step === 4 && documenti && (
          <OnboardingStep4
            control={control}
            setValue={setValue}
            getValues={getValues}
            errors={formState.errors}
            documenti={documenti}
            voci={voci}
            documentiSenzaRisposta={documentiSenzaRisposta}
            segnalazioneSeq={segnalazioneSeq}
          />
        )}
        {step === 4 && !documenti && !documentiError && (
          <div role="status" aria-live="polite" className="py-8 text-center">
            <p className="text-text-secondary">Caricamento dei documenti...</p>
          </div>
        )}
        {step === 4 && documentiError && (
          <div role="alert" className="py-8 text-center flex flex-col gap-3 items-center">
            <p className="text-text-secondary">
              Non riusciamo a mostrare i documenti in questo momento.
            </p>
            <button
              type="button"
              onClick={() => void loadDocumenti()}
              className="min-h-[44px] px-5 rounded-lg border border-outline text-text-main bg-surface-container-lowest font-medium"
            >
              Riprova
            </button>
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-3 mt-8">
          {step > 1 && (
            <button
              type="button"
              onClick={handlePrev}
              disabled={submitting}
              className="min-h-[44px] px-5 rounded-lg border border-outline text-text-main bg-surface-container-lowest font-medium hover:bg-surface-container disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Indietro
            </button>
          )}
          <div className="flex-1" />
          {step < 4 && (
            <button
              type="button"
              onClick={handleNext}
              className="min-h-[44px] px-6 rounded-lg bg-primary text-on-primary font-medium hover:bg-primary-container disabled:opacity-50"
            >
              Avanti
            </button>
          )}
          {step === 4 && (
            <button
              type="submit"
              disabled={submitting || !documenti}
              className="min-h-[44px] px-6 rounded-lg bg-primary text-on-primary font-medium hover:bg-primary-container disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting
                ? 'Invio in corso...'
                : 'Confermo presa visione e scelte'}
            </button>
          )}
        </div>
      </form>
    </OnboardingShell>
  );
}
